import { useState, useEffect } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Edit, Trash2, Phone, Mail, Stethoscope, User } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PhysiciansPage() {
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPhysician, setEditingPhysician] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    npi: "",
    specialty: "",
    phone: "",
    fax: "",
    email: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
  });

  useEffect(() => {
    fetchPhysicians();
  }, []);

  const fetchPhysicians = async () => {
    try {
      const response = await axios.get(`${API}/physicians`);
      setPhysicians(response.data);
    } catch (error) {
      console.error("Failed to fetch physicians:", error);
      toast.error("Failed to load physicians");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      npi: "",
      specialty: "",
      phone: "",
      fax: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
    });
    setEditingPhysician(null);
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (physician) => {
    setEditingPhysician(physician);
    setFormData({
      name: physician.name || "",
      npi: physician.npi || "",
      specialty: physician.specialty || "",
      phone: physician.phone || "",
      fax: physician.fax || "",
      email: physician.email || "",
      address: physician.address || "",
      city: physician.city || "",
      state: physician.state || "",
      zip_code: physician.zip_code || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.error("Physician name is required");
      return;
    }

    setSaving(true);
    try {
      if (editingPhysician) {
        await axios.patch(`${API}/physicians/${editingPhysician.id}`, formData);
        toast.success("Physician updated!");
      } else {
        await axios.post(`${API}/physicians`, formData);
        toast.success("Physician added!");
      }
      setDialogOpen(false);
      resetForm();
      fetchPhysicians();
    } catch (error) {
      console.error("Failed to save physician:", error);
      toast.error("Failed to save physician");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (physicianId) => {
    if (!confirm("Are you sure you want to delete this physician?")) return;
    
    try {
      await axios.delete(`${API}/physicians/${physicianId}`);
      toast.success("Physician deleted");
      fetchPhysicians();
    } catch (error) {
      console.error("Failed to delete physician:", error);
      toast.error("Failed to delete physician");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Physicians</h1>
            <p className="text-slate-500">Manage your agency's physician directory</p>
          </div>
          <Button onClick={openNewDialog} className="gap-2" data-testid="add-physician-btn">
            <Plus className="w-4 h-4" />
            Add Physician
          </Button>
        </div>

        {/* Physicians Table */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : physicians.length === 0 ? (
            <div className="p-12 text-center">
              <Stethoscope className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No physicians added yet</p>
              <p className="text-sm text-slate-400 mt-1">Add physicians to quickly select them in forms</p>
              <Button onClick={openNewDialog} variant="outline" className="mt-4 gap-2">
                <Plus className="w-4 h-4" />
                Add First Physician
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>NPI</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Fax</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {physicians.map((physician) => (
                  <TableRow key={physician.id} data-testid={`physician-row-${physician.id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        {physician.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-600">{physician.npi || "-"}</TableCell>
                    <TableCell className="text-slate-600">{physician.specialty || "-"}</TableCell>
                    <TableCell className="text-slate-600">
                      {physician.phone ? (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {physician.phone}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-slate-600">{physician.fax || "-"}</TableCell>
                    <TableCell className="text-slate-600">
                      {physician.email ? (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {physician.email}
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(physician)}
                          data-testid={`edit-physician-${physician.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(physician.id)}
                          data-testid={`delete-physician-${physician.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {/* Add/Edit Physician Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingPhysician ? "Edit Physician" : "Add New Physician"}</DialogTitle>
            <DialogDescription>
              {editingPhysician ? "Update physician information" : "Add a new physician to your directory"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Dr. John Smith"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="npi">NPI Number</Label>
                <Input
                  id="npi"
                  placeholder="1234567890"
                  value={formData.npi}
                  onChange={(e) => setFormData({ ...formData, npi: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="specialty">Specialty</Label>
              <Input
                id="specialty"
                placeholder="Internal Medicine, Cardiology, etc."
                value={formData.specialty}
                onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="555-123-4567"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fax">Fax</Label>
                <Input
                  id="fax"
                  placeholder="+12025551234"
                  value={formData.fax}
                  onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="doctor@hospital.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="123 Medical Center Dr"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  placeholder="Springfield"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  placeholder="IL"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  placeholder="62701"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving ? "Saving..." : editingPhysician ? "Update" : "Add Physician"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
